<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { debounce } from '@/utils/debounce';
import { useI18n } from 'vue-i18n';
import { getProducts } from '@/api/Products';
import LoadingOverlay from './utilities/LoadingOverlay.vue';
import ProductCard from './ProductCard.vue';
import Search from './Search.vue';
import Pager from './Pager.vue';
import AngleDown from './svg/AngleDown.vue';
import IconGrid from './svg/IconGrid.vue';
import IconList from './svg/IconList.vue';

const { locale } = useI18n();

const props = defineProps<{
  rootKey: string,
  parentKey: string
}>();

const { t } = useI18n();

const filters = ref<ProductsFilters>({
  page: ref<number>(1),
  pageSize: ref<number>(12),
  searchTerm: undefined,
  culture: locale.value
});

const productRequestIsLoading = ref<boolean>(false);
const productResults = ref<ProductListItemResponse[]>();
const resultCount = ref<number>(0);

const listOptions = [12, 24, 48];
const selectedListOption = ref<number>(listOptions[0]);
const viewType = ref<string>('grid');

function getResultsText(): string {
  const start = (filters.value.page - 1) * filters.value.pageSize + 1;
  const end = Math.min(filters.value.page * filters.value.pageSize, resultCount.value);
  return `${start}-${end} ${t('resultsOf')} ${resultCount.value}`;
}

function handleSubmitSearchTerm(term: string) {
  filters.value.searchTerm = term;
}

async function updateProductList(request: GetProductsRequest) {
  productRequestIsLoading.value = true;
  const data = await getProducts(request);
  productRequestIsLoading.value = false;
  /* @ts-expect-error: startViewTransition n'est encore qu'au stade de CR (Candidate release) et non disponible dans les d�finitions typescripts encore */
  if (!document.startViewTransition) {
    productResults.value = data.data;
    resultCount.value = data.totalRecords;
    return;
  }

  /* @ts-expect-error: startViewTransition n'est encore qu'au stade de CR (Candidate release) et non disponible dans les d�finitions typescripts encore */
  document.startViewTransition(() => {
    productResults.value = data.data;
    resultCount.value = data.totalRecords;
  });
}

const debouncedUpdateProductList = debounce(updateProductList, 200);

watch(filters, async (value, oldValue) => {
  const request: GetProductsRequest = {
    rootKey: props.rootKey,
    parentKey: props.parentKey,
    page: filters.value.page,
    pageSize: filters.value.pageSize,
    searchTerm: filters.value.searchTerm,
    culture: locale.value
  };

  debouncedUpdateProductList(request);
}, {
  deep: true
});

watch(selectedListOption, (newValue) => {
  filters.value.pageSize = ref<number>(newValue);
  localStorage.setItem('selectedListOption', newValue.toString());
});

watch(viewType, (newValue) => {
  localStorage.setItem('selectedViewType', newValue);
});

onMounted(() => {
  const savedOption = localStorage.getItem('selectedListOption');
  if (savedOption && listOptions.includes(parseInt(savedOption))) {
    selectedListOption.value = parseInt(savedOption);
    filters.value.pageSize = ref<number>(selectedListOption.value);
  }

  const savedViewType = localStorage.getItem('selectedViewType');
  if (savedViewType && ['grid', 'list'].includes(savedViewType)) {
    viewType.value = savedViewType;
  }

  const initialRequest: GetProductsRequest = {
    rootKey: props.rootKey,
    parentKey: props.parentKey,
    page: filters.value.page,
    pageSize: filters.value.pageSize,
    searchTerm: filters.value.searchTerm,
    culture: locale.value,
    currentProductCount: '',
  };

  updateProductList(initialRequest);
});
</script>

<template>
  <div class="productList">
    <div class="listOptions">
      <div class="listOptions-left d">
        <div class="listOptions-count">
          {{ getResultsText() }}
        </div>
      </div>
      <div class="listOptions-right">
        <div class="listOptions-count m">
          <span class="listOptions-currentItems">{{ getResultsText() }}</span>
        </div>
        <div class="listOptions-option listOptions-option_perPage">
          <div class="listOptions-label">{{ $t("perPage") }}</div>
          <div class="listOptions-select fakeSelect">
            <select v-model="selectedListOption">
              <option v-for="option in listOptions" :key="option" :value="option">{{ option }}</option>
            </select>
            <AngleDown></AngleDown>
          </div>
        </div>
        <div class="listOptions-option listOptions-option_view">
          <div class="listOptions-label">{{ $t("view") }}</div>
          <div class="listOptions-radios">
            <label class="listOptions-viewOption">
              <input type="radio" name="views" value="grid" v-model="viewType" />
              <span><IconGrid></IconGrid></span>
            </label>
            <label class="listOptions-viewOption">
              <input type="radio" name="views" value="list" v-model="viewType" />
              <span><IconList></IconList></span>
            </label>
          </div>
        </div>
        <div class="listOptions-option listOptions-option_search">
          <Search @submit:search-term="handleSubmitSearchTerm" @reset:search-term="handleResetSearchTerm"></Search>
        </div>
      </div>
    </div>
    <LoadingOverlay v-if="productRequestIsLoading"></LoadingOverlay>
    <div v-if="productResults?.length > 0" :class="['productList-wrapper', viewType === 'grid' ? 'productList-wrapper_gridView' : 'productList-wrapper_listView']">
      <ProductCard v-for="product in productResults" :key="product.id" :product="product" :class="viewType === 'grid' ? 'card_grid' : 'card_list'"></ProductCard>
    </div>
    <div v-else class="productList-wrapper">{{ $t("noProducts") }}</div>
  </div>
  <Pager v-if="productResults?.length"
       v-model="filters.page"
       :total-items="resultCount"
       :page-size="filters.pageSize">
  </Pager>
</template>

<style lang="scss">
</style>
